import { portalAPI } from './portalAPI';

import type { Team, TeamCreateDto, TeamMinDto, TeamUpdateDto } from 'types/teams';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const APITeamsSlice = portalAPI.enhanceEndpoints({ addTagTypes: ['Teams', 'TeamsMin'] }).injectEndpoints({
    endpoints: (builder) => ({
        getTeamById: builder.query<Team, string>({
            query: (id) => `/v1/teams/${id}`,
            keepUnusedDataFor: 0,
        }),
        getTeamsList: builder.query<Team[], void>({
            query: () => '/v1/teams',
            // Load the teams list only once in 10 minutes
            keepUnusedDataFor: 10 * 60,
            providesTags: ['Teams'],
        }),
        getHasSectorGroups: builder.query<boolean, void>({
            query: () => '/v1/teams/has-sector-groups',
        }),
        getMinTeamsListWithFilters: accumulatedQueryWithFilters<TeamMinDto>(builder, '/v1/teams/min', {
            providesTags: ['TeamsMin'],
        }),
        getTeamsWithFilters: accumulatedQueryWithFilters<Team>(builder, '/v1/teams', {
            providesTags: ['Teams'],
        }),
        addTeam: builder.mutation<Team, TeamCreateDto>({
            query: ({ ...body }) => ({
                url: `/v1/teams`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Teams'],
        }),
        updateTeam: builder.mutation<Team, TeamUpdateDto>({
            query: ({ id, ...body }) => ({
                url: `/v1/teams/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Teams'],
        }),
        deleteTeam: builder.mutation<boolean, { id: string }>({
            query: ({ id }) => ({
                url: `/v1/teams/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Teams'],
        }),
    }),
    overrideExisting: false,
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetTeamByIdQuery,
    useGetHasSectorGroupsQuery,
    useLazyGetMinTeamsListWithFiltersQuery,
    useGetMinTeamsListWithFiltersQuery,
    useLazyGetTeamsWithFiltersQuery,
    useAddTeamMutation,
    useUpdateTeamMutation,
} = APITeamsSlice;
